"use client";

import { useState } from "react";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getDevAdvancedCopy } from "@/lib/dev-advanced-copy";
import { getToolUiCopy } from "@/lib/tool-ui-copy";
import { tools } from "@/config/tools";
import { getLocalizedTool } from "@/lib/tool-localization";

export function PasswordGenerator() {
  const [password, setPassword] = useState("");
  const [length, setLength] = useState(16);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const copy = getDevAdvancedCopy(locale);
  const ui = getToolUiCopy(locale);
  const localizedTool = getLocalizedTool(
    tools.find((item) => item.id === "password-generator") ?? tools[0],
    locale
  );

  const generatePassword = () => {
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const symbols = "!@#$%^&*()_+-=[]{}|;:,.<>?";

    let chars = "";
    if (includeUppercase) chars += uppercase;
    if (includeLowercase) chars += lowercase;
    if (includeNumbers) chars += numbers;
    if (includeSymbols) chars += symbols;

    if (chars === "") {
      chars = lowercase; // Default to lowercase if nothing selected
    }

    let generatedPassword = "";
    const array = new Uint32Array(length);
    crypto.getRandomValues(array);

    for (let i = 0; i < length; i++) {
      generatedPassword += chars[array[i] % chars.length];
    }

    setPassword(generatedPassword);
    calculateStrength(generatedPassword);
  };

  const calculateStrength = (pwd: string) => {
    let strength = 0;
    if (pwd.length >= 8) strength += 20;
    if (pwd.length >= 12) strength += 20;
    if (pwd.length >= 16) strength += 10;
    if (/[a-z]/.test(pwd)) strength += 15;
    if (/[A-Z]/.test(pwd)) strength += 15;
    if (/[0-9]/.test(pwd)) strength += 10;
    if (/[^a-zA-Z0-9]/.test(pwd)) strength += 10;
    setPasswordStrength(Math.min(strength, 100));
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(password);
  };

  const getStrengthColor = () => {
    if (passwordStrength < 40) return "bg-red-500";
    if (passwordStrength < 70) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getStrengthText = () => {
    if (passwordStrength < 40) return copy.weak;
    if (passwordStrength < 70) return copy.medium;
    return copy.strong;
  };

  return (
    <div className="space-y-8">
      <div className="rounded-[20px] border border-positivus-dark/10 bg-positivus-green/10 p-6">
        <div className="flex items-start gap-4">
           <div className="hidden md:block rounded-full bg-positivus-green/20 p-2">
            <Icon icon="mdi:lock-outline" className="h-6 w-6 text-positivus-dark" />
          </div>
          <div className="text-sm text-positivus-dark w-full">
            <p className="font-medium text-lg mb-2">{localizedTool.name}</p>
            <p className="leading-relaxed">{localizedTool.description}</p>
          </div>
        </div>
      </div>

      <div className="space-y-8">
        <div className="rounded-[20px] border-2 border-positivus-dark bg-white p-6 shadow-[0_5px_0_0_#191A23]">
           <div className="flex justify-between items-center mb-6">
               <label className="text-lg font-bold text-positivus-dark">{copy.passwordLength}</label>
               <span className="font-black text-2xl text-positivus-green bg-positivus-green/10 px-4 py-2 rounded-[15px]">{length}</span>
           </div>
          <input
            type="range"
            min="8"
            max="64"
            value={length}
            onChange={(e) => setLength(parseInt(e.target.value))}
            className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-positivus-dark"
          />
           <div className="flex justify-between text-xs font-bold text-gray-400 mt-2">
                 <span>{copy.minimum} (8)</span>
                 <span>{copy.maximum} (64)</span>
            </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className={`flex items-center gap-4 rounded-[15px] border-2 p-5 transition-all cursor-pointer ${includeUppercase ? 'border-positivus-dark bg-positivus-dark text-white shadow-[0_5px_0_0_#191A23] translate-y-[-2px]' : 'border-positivus-dark/20 bg-white hover:border-positivus-dark text-positivus-dark'}`}>
            <div className={`flex items-center justify-center p-1 rounded-md border-2 ${includeUppercase ? 'border-white bg-white/20' : 'border-gray-300'}`}>
                {includeUppercase && <Icon icon="mdi:check" className="h-4 w-4" />}
                {!includeUppercase && <div className="h-4 w-4" />}
            </div>
            <input
              type="checkbox"
              checked={includeUppercase}
              onChange={(e) => setIncludeUppercase(e.target.checked)}
              className="hidden"
            />
            <span className="text-lg font-bold">{copy.includeUppercase} (A-Z)</span>
          </label>

          <label className={`flex items-center gap-4 rounded-[15px] border-2 p-5 transition-all cursor-pointer ${includeLowercase ? 'border-positivus-dark bg-positivus-dark text-white shadow-[0_5px_0_0_#191A23] translate-y-[-2px]' : 'border-positivus-dark/20 bg-white hover:border-positivus-dark text-positivus-dark'}`}>
            <div className={`flex items-center justify-center p-1 rounded-md border-2 ${includeLowercase ? 'border-white bg-white/20' : 'border-gray-300'}`}>
                {includeLowercase && <Icon icon="mdi:check" className="h-4 w-4" />}
                {!includeLowercase && <div className="h-4 w-4" />}
            </div>
             <input
              type="checkbox"
              checked={includeLowercase}
              onChange={(e) => setIncludeLowercase(e.target.checked)}
              className="hidden"
            />
            <span className="text-lg font-bold">{copy.includeLowercase} (a-z)</span>
          </label>

          <label className={`flex items-center gap-4 rounded-[15px] border-2 p-5 transition-all cursor-pointer ${includeNumbers ? 'border-positivus-dark bg-positivus-dark text-white shadow-[0_5px_0_0_#191A23] translate-y-[-2px]' : 'border-positivus-dark/20 bg-white hover:border-positivus-dark text-positivus-dark'}`}>
             <div className={`flex items-center justify-center p-1 rounded-md border-2 ${includeNumbers ? 'border-white bg-white/20' : 'border-gray-300'}`}>
                {includeNumbers && <Icon icon="mdi:check" className="h-4 w-4" />}
                {!includeNumbers && <div className="h-4 w-4" />}
            </div>
            <input
              type="checkbox"
              checked={includeNumbers}
              onChange={(e) => setIncludeNumbers(e.target.checked)}
              className="hidden"
            />
            <span className="text-lg font-bold">{copy.includeNumbers} (0-9)</span>
          </label>

          <label className={`flex items-center gap-4 rounded-[15px] border-2 p-5 transition-all cursor-pointer ${includeSymbols ? 'border-positivus-dark bg-positivus-dark text-white shadow-[0_5px_0_0_#191A23] translate-y-[-2px]' : 'border-positivus-dark/20 bg-white hover:border-positivus-dark text-positivus-dark'}`}>
             <div className={`flex items-center justify-center p-1 rounded-md border-2 ${includeSymbols ? 'border-white bg-white/20' : 'border-gray-300'}`}>
                {includeSymbols && <Icon icon="mdi:check" className="h-4 w-4" />}
                {!includeSymbols && <div className="h-4 w-4" />}
            </div>
            <input
              type="checkbox"
              checked={includeSymbols}
              onChange={(e) => setIncludeSymbols(e.target.checked)}
              className="hidden"
            />
            <span className="text-lg font-bold">{copy.includeSymbols} (!@#$)</span>
          </label>
        </div>

        <button
          onClick={generatePassword}
          className="w-full rounded-[20px] bg-positivus-dark px-6 py-5 text-xl font-bold text-white hover:bg-positivus-green hover:text-positivus-dark transition-all shadow-[0_5px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px]"
        >
          <div className="flex items-center justify-center gap-3">
             <Icon icon="mdi:auto-fix" className="h-6 w-6" />
             <span>{copy.generatePassword}</span>
          </div>
        </button>
      </div>

      {password && (
        <div className="space-y-6">
          <div>
            <div className="mb-3 flex items-center justify-between">
              <label className="text-lg font-bold text-positivus-dark">{copy.generatedPassword}</label>
              <button
                onClick={handleCopy}
                className="flex items-center gap-2 rounded-[10px] bg-positivus-green/20 px-3 py-1.5 text-sm font-bold text-positivus-dark hover:bg-positivus-green hover:text-white transition-colors"
              >
                <Icon icon={uiIcons.copy} className="h-4 w-4" />
                {ui.copy}
              </button>
            </div>
            <div className="rounded-[20px] border-2 border-positivus-dark bg-gray-50 p-6 relative group cursor-pointer hover:bg-white transition-colors" onClick={handleCopy}>
              <p className="break-all font-mono text-2xl text-center text-positivus-dark tracking-wider select-all">{password}</p>
               <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/5 rounded-[18px] pointer-events-none">
                  <span className="bg-black/70 text-white text-xs px-2 py-1 rounded">{copy.clickToCopy}</span>
               </div>
            </div>
          </div>

          <div className="rounded-[20px] border-2 border-positivus-dark/10 bg-white p-6">
            <div className="mb-3 flex items-center justify-between">
              <span className="font-bold text-positivus-dark">{copy.passwordStrength}</span>
              <span className={`text-sm font-black uppercase tracking-wider px-3 py-1 rounded-full ${passwordStrength < 40 ? "bg-red-100 text-red-600" : passwordStrength < 70 ? "bg-yellow-100 text-yellow-600" : "bg-green-100 text-green-600"}`}>
                {getStrengthText()}
              </span>
            </div>
            <div className="h-4 w-full rounded-full bg-gray-200 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ease-out ${getStrengthColor()}`}
                style={{ width: `${passwordStrength}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-gray-400 font-medium">{copy.passwordStrengthHint}</p>
          </div>
        </div>
      )}
    </div>
  );
}
